import type { Language } from '@seek/forms-ui/lib/types';
import type { Brand } from '@seek/melways-sites';
import { seek_com_au } from '@seek/online-identity';
import React from 'react';

import type { Customisations } from '../modules/customisation';
import { type Environment, Experience } from '../types';
import getClientHrefLinks from '../utils/getClientHrefLinks';
import getHashRoute from '../utils/getHashRoute';

import { REGISTER } from './pages/LoginPage/hashRoutes';

export interface AppContextType extends Customisations {
  isStaticRender: boolean;
  site: string;
  environment: Environment;
  language: Language;
  locale: string;
  brand?: Brand;
  isMobileClient?: boolean;
}

const AppContext = React.createContext<AppContextType>({
  isStaticRender: false,
  environment: 'development',
  site: 'seekAnz',
  siteName: 'SEEK',
  siteNameOnFooter: 'SEEK',
  realm: 'Username-Password-Authentication',
  tenant: 'seekanz',
  enableNudge: true,
  enableRealmHint: false,
  clientOrigin: 'https://www.seek.com.au',
  language: 'en',
  locale: 'au',
  experience: Experience.Candidate,
  brand: seek_com_au.brand,
  isMobileClient: false,
});

export const useAppContext = () => {
  const context = React.useContext(AppContext);

  if (context === undefined) {
    throw new Error('appState must be used within a AppProvider');
  }

  const getClientHref = (path = '/', experience = '') =>
    getClientHrefLinks(path, context, experience);

  const registerHref = context.clientRegisterPath
    ? getClientHref(context.clientRegisterPath)
    : getHashRoute(REGISTER);

  return { ...context, getClientHref, registerHref };
};

export default AppContext;
